import { Popover } from 'antd'
import clsx from 'clsx'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { INavGroup, isNavGroupActive } from '../Navigation.const'
import { ChevronIcon } from '../../../../../../shared/components/icons/nav/ChevronIcon'
import { NavIcon } from './NavIcon'
import { getNavItemClassName } from './NavItemClassName'
import { NavSections } from './NavSections'

/** Поповер antd по умолчанию светлый — приводим его к палитре меню */
const POPOVER_OVERLAY_CLASS = clsx(
  '[&_.ant-popover-inner]:rounded-[6px]',
  '[&_.ant-popover-inner]:shadow-[0px_0px_1px_rgba(14,99,216,0.65),0px_4px_20px_rgba(0,50,109,0.24)]',
  '[&_.ant-popover-inner-content]:p-[8px]',
  '[&_.ant-popover-arrow-content]:[--antd-arrow-background-color:#00326D]',
)

/**
 * Длительность растёт медленнее высоты: при фиксированной длительности длинный список
 * пролетал бы быстрее короткого, а при строго пропорциональной — короткий закрывался бы
 * заметно раньше. Корень даёт середину: время близкое, скорость без резких скачков
 */
const BASE_OPEN_MS = 90
const OPEN_MS_PER_SQRT_PX = 9
const MAX_OPEN_MS = 260

const getOpenDuration = (height: number) =>
  Math.round(Math.min(BASE_OPEN_MS + OPEN_MS_PER_SQRT_PX * Math.sqrt(height), MAX_OPEN_MS))

export interface INavMenuGroupProps {
  group: INavGroup
  /** Раскрыт ли вложенный список в развёрнутом меню */
  open: boolean
  /** Свёрнутое меню: список показываем не под пунктом, а в поповере справа */
  collapsed: boolean
  onToggle: () => void
  /** Обработчик вложенных пунктов без маршрута (logout и т.п.) */
  onAction?: (id: string) => void
}

export const NavMenuGroup = ({
  group,
  open,
  collapsed,
  onToggle,
  onAction,
}: INavMenuGroupProps) => {
  const { t } = useTranslation()
  const Icon = group.icon
  const Menu = group.menu
  const [popoverOpen, setPopoverOpen] = useState(false)
  const [openDuration, setOpenDuration] = useState(BASE_OPEN_MS)
  const contentRef = useRef<HTMLDivElement>(null)

  const { pathname } = useLocation()

  const isOpen = open && !collapsed
  const isActive = isNavGroupActive(group, pathname)
  const showPopover = collapsed && popoverOpen

  useEffect(() => {
    if (!collapsed) setPopoverOpen(false)
  }, [collapsed])

  // Содержимое лежит в свёрнутой строке грида, но собственную высоту сохраняет,
  // поэтому её можно измерить заранее — в том числе когда список догрузился (Help)
  useEffect(() => {
    const content = contentRef.current

    if (!content) return

    const updateDuration = () => setOpenDuration(getOpenDuration(content.offsetHeight))

    updateDuration()

    if (typeof ResizeObserver === 'undefined') return

    const observer = new ResizeObserver(updateDuration)

    observer.observe(content)

    return () => observer.disconnect()
  }, [])

  return (
    <div className={'flex flex-col'}>
      <Popover
        content={
          <div className={'w-[226px]'}>
            {Menu ? (
              <Menu light onSelect={() => setPopoverOpen(false)} />
            ) : (
              <NavSections
                light
                sections={group.sections ?? []}
                onAction={onAction}
                onNavigate={() => setPopoverOpen(false)}
              />
            )}
          </div>
        }
        open={showPopover}
        overlayClassName={POPOVER_OVERLAY_CLASS}
        placement={'rightTop'}
        showArrow={false}
        trigger={'hover'}
        onOpenChange={(nextOpen) => collapsed && setPopoverOpen(nextOpen)}
      >
        <button
          className={clsx(getNavItemClassName({ active: showPopover || isActive, collapsed }), {
            ['pr-[12px]']: !collapsed,
          })}
          onClick={() => !collapsed && onToggle()}
        >
          <NavIcon icon={Icon} />
          <span className={clsx('leading-[24px]', { ['hidden']: collapsed })}>{t(group.label)}</span>
          <span
            className={clsx('ml-auto transition-transform duration-150', {
              ['hidden']: collapsed,
              ['rotate-180']: isOpen,
            })}
          >
            <ChevronIcon />
          </span>
        </button>
      </Popover>
      <div
        className={clsx('grid transition-all', {
          ['grid-rows-[1fr]']: isOpen,
          ['grid-rows-[0fr]']: !isOpen,
        })}
        style={{ transitionDuration: `${openDuration}ms` }}
      >
        <div className={'overflow-hidden'}>
          <div ref={contentRef}>
            {Menu ? <Menu /> : <NavSections sections={group.sections ?? []} onAction={onAction} />}
          </div>
        </div>
      </div>
    </div>
  )
}
